// Applies the player's colour theme and light/dark choice. The theme's colours are set as CSS
// variables on the page root (overriding app.css); the app's own look ("nykyinen") uses app.css
// as it is. The choice is also kept on this device so the next start paints the right colours
// before the profile has loaded.
import { useEffect, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from './api.ts';
import { useSession } from './session.tsx';
import { BASE_THEME, DEFAULT_THEME, findTheme, THEME_TOKENS, type ColorMode, type Palette, type Theme } from '../../shared/themes.ts';

const CACHE_KEY = 'kotipoker.theme';
const dark = () => window.matchMedia('(prefers-color-scheme: dark)');

/** a + (b − a) × t for two #rrggbb colours. */
function mix(a: string, b: string, t: number): string {
  const n = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [x, y] = [n(a), n(b)];
  return `#${x.map((v, i) => Math.round(v + (y[i]! - v) * t).toString(16).padStart(2, '0')).join('')}`;
}

/** The theme's tokens plus the few app.css derives from them (hover, soft fills, ink accents). */
function variables(theme: Theme, mode: 'light' | 'dark'): Record<string, string> {
  const p: Palette = theme[mode];
  const toward = mode === 'light' ? '#000000' : '#ffffff';
  const vars: Record<string, string> = {};
  for (const k of THEME_TOKENS) vars[`--${k}`] = p[k];
  Object.assign(vars, {
    '--primary-hover': mix(p.primary, toward, 0.12),
    '--line-strong': mix(p.line, p.ink, 0.18),
    '--brass-ink': mode === 'light' ? mix(p.brass, p.ink, 0.45) : mix(p.brass, '#ffffff', 0.45),
    '--accent-ink': mode === 'light' ? mix(p.primary, p.ink, 0.15) : mix(p.primary, '#ffffff', 0.35),
    '--ok-soft': mix(p.paper, p.ok, 0.14),
    '--danger-soft': mix(p.paper, p.danger, 0.12),
    '--warn-soft': p['brass-soft'],
    '--bubble-mine-ink': p.ink,
    '--radius-s': `${Math.max(2, Math.round(theme.radius * 0.6))}px`,
    '--radius-m': `${theme.radius}px`,
    '--radius-l': `${Math.round(theme.radius * 1.6)}px`,
  });
  return vars;
}

let applied: string[] = [];
let current = { theme: DEFAULT_THEME, mode: 'system' as ColorMode, resolved: 'light' as 'light' | 'dark' };
const listeners = new Set<() => void>();

export function applyTheme(themeId: string, mode: ColorMode): void {
  const root = document.documentElement;
  const theme = findTheme(themeId);
  const resolved = mode === 'system' ? (dark().matches ? 'dark' : 'light') : mode;
  if (mode === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', mode);
  for (const name of applied) root.style.removeProperty(name);
  applied = [];
  if (theme.id !== BASE_THEME) {
    for (const [name, value] of Object.entries(variables(theme, resolved))) {
      root.style.setProperty(name, value);
      applied.push(name);
    }
  }
  root.style.colorScheme = resolved;
  // The phone's status bar follows the table colour.
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme.id === BASE_THEME ? '#0f4a4f' : theme[resolved].felt);
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ theme: theme.id, mode }));
  } catch {
    // no storage: the profile brings the choice back after loading
  }
  current = { theme: theme.id, mode, resolved };
  for (const l of listeners) l();
}

/** Paints the last choice on this device right away (before the profile loads). */
export function applyCachedTheme(): void {
  try {
    const c = JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null') as { theme?: string; mode?: ColorMode } | null;
    applyTheme(c?.theme ?? DEFAULT_THEME, c?.mode ?? 'system');
  } catch {
    // nothing cached
  }
}

/** Keeps the page in the signed-in player's theme, and follows the device when on "system". */
export function useThemeSync(): void {
  const { me } = useSession();
  const theme = me?.uiPrefs?.theme;
  const mode = me?.uiPrefs?.colorMode;
  useEffect(() => {
    if (!theme || !mode) return;
    applyTheme(theme, mode);
    if (mode !== 'system') return;
    const mq = dark();
    const on = () => applyTheme(theme, mode);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [theme, mode]);
}

/** Changes whenever the colours on screen change (for charts that read CSS variables). */
export function useThemeKey(): string {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      const mq = dark();
      mq.addEventListener('change', l);
      return () => {
        listeners.delete(l);
        mq.removeEventListener('change', l);
      };
    },
    () => `${current.theme}:${current.mode === 'system' ? (dark().matches ? 'dark' : 'light') : current.resolved}`,
  );
}

/** The player's choice, saved to the profile (so every device follows it). */
export function useThemeSetting(): { theme: string; mode: ColorMode; setTheme: (id: string) => Promise<void>; setMode: (m: ColorMode) => Promise<void> } {
  const { me } = useSession();
  const qc = useQueryClient();
  const theme = me?.uiPrefs?.theme ?? DEFAULT_THEME;
  const mode = me?.uiPrefs?.colorMode ?? 'system';
  const save = async (patch: { theme?: string; colorMode?: ColorMode }) => {
    qc.setQueryData(['me'], (old: typeof me) => (old ? { ...old, uiPrefs: { ...old.uiPrefs, ...patch } } : old));
    try {
      await api.patch('/api/me', { uiPrefs: patch });
    } catch {
      // not saved: the refetch below brings back the stored choice
    } finally {
      await qc.invalidateQueries({ queryKey: ['me'] });
    }
  };
  return { theme, mode, setTheme: (id) => save({ theme: id }), setMode: (m) => save({ colorMode: m }) };
}
